import { z } from 'zod';
import { boardParams, endpoint, isoDate, isTimeZone } from './common';

// A spoken summary on the kiosk page, for one day, this week or next week: the board's tasks for that stretch go to a language
// model, which writes a short summary, and a text-to-speech voice reads it aloud.

export const briefingPeriod = z.enum(['day', 'week', 'nextWeek']);
export type BriefingPeriod = z.infer<typeof briefingPeriod>;

const HOUR_MS = 60 * 60 * 1000;

// How long each period may be, give or take a clock change (a day of 23 or 25 hours, a week of 167 or 169).
const LENGTH_HOURS: Record<BriefingPeriod, [number, number]> = {
    day: [22, 26],
    week: [6 * 24, 8 * 24],
    nextWeek: [6 * 24, 8 * 24],
};

export const generateBriefing = endpoint({
    method: 'POST',
    path: '/boards/:boardId/briefing',
    params: boardParams,
    // The caller knows where its days and weeks start in its own time zone, so it names the stretch to cover: [start, end).
    body: z.object({
        period: briefingPeriod,
        start: isoDate,
        end: isoDate,
        timeZone: z.string().refine(isTimeZone, 'timeZone must be a valid IANA time zone'),
    }).superRefine((body, context) => {
        const hours = (Date.parse(body.end) - Date.parse(body.start)) / HOUR_MS;
        const [least, most] = LENGTH_HOURS[body.period];

        if (!(hours >= least && hours <= most)) {
            context.addIssue({ code: 'custom', path: ['end'], message: `a ${body.period === 'day' ? 'day' : 'week'} must run from start to end, about ${body.period === 'day' ? 'a day' : 'seven days'} long` });
        }
    }),
    response: z.object({
        summary: z.string(),                    // what is read aloud, so it can also be shown
        audio: z.string(),                      // the recording, base64
        contentType: z.literal('audio/mpeg'),
    }),
});
